import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Eye, Send, Sparkles, Save } from "lucide-react";

const FALLBACK_VARS = {
  clinic_welcome: { contact_name: "Dr. Anya", clinic_name: "Apollo Care Clinic", contact_email: "anya@apollo.com", temp_password: "abc123", login_url: "https://app.pulse.ai/login" },
  payment_received: { doctor_name: "Anya", patient_name: "Priya Verma", amount: 1500, currency: "₹", visit_date: "2026-05-10" },
  payment_reminder: { patient_name: "Priya Verma", clinic_name: "Apollo Care Clinic", amount: 1500, currency: "₹", visit_date: "2026-05-10", pay_link: "https://pay/abc" },
  medication_followup: { patient_name: "Priya", doctor_name: "Anya", clinic_name: "Apollo Care", reply_better: "#", reply_same: "#", reply_worse: "#" },
  prescription_ready: { patient_name: "Priya Verma", doctor_name: "Anya Sharma", clinic_name: "Apollo Care", prescription_link: "https://app/rx/abc" },
};

export default function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = () => api.get("/super-admin/email-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!active) { setDraft(null); setPreview(null); return; }
    const t = templates.find((x) => x.key === active);
    setDraft(t ? { ...t } : null);
    refreshPreview(t);
  }, [active]); // eslint-disable-line

  const refreshPreview = async (t) => {
    if (!t) return;
    try {
      const r = await api.post(`/super-admin/email-templates/${t.key}/preview`, {
        variables: FALLBACK_VARS[t.key] || {},
      });
      setPreview(r.data);
    } catch { /* ignore */ }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await api.post("/super-admin/email-templates", {
        key: draft.key, name: draft.name, subject: draft.subject,
        body_html: draft.body_html, variables: draft.variables || [],
      });
      toast.success("Template saved");
      await load();
      refreshPreview(draft);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <aside className="solid-panel p-0 overflow-hidden shadow-3d">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-[#004a99]" /> Templates</h3>
          <span className="tiny-label">{templates.length}</span>
        </div>
        <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
          {templates.map((t) => (
            <button key={t.key}
                    onClick={() => setActive(t.key)}
                    className={`w-full text-left px-5 py-3 transition ${active === t.key ? "bg-[#e6f0ff] border-l-4 border-[#004a99]" : "hover:bg-slate-50"}`}
                    data-testid={`tpl-row-${t.key}`}>
              <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
              <div className="text-xs text-slate-500 truncate">{t.subject}</div>
            </button>
          ))}
        </div>
      </aside>

      <section className="lg:col-span-2 solid-panel p-6 shadow-3d">
        {!draft && (
          <div className="text-center py-16 text-slate-400">
            <Sparkles className="h-10 w-10 mx-auto opacity-40 mb-2" />
            <div>Pick a template on the left to start editing.</div>
          </div>
        )}
        {draft && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="tiny-label">{draft.is_default ? "Default Template" : "Custom Template"}</div>
                <h3 className="text-2xl font-heading font-semibold">{draft.name}</h3>
              </div>
              <div className="flex gap-2">
                <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#004a99] hover:bg-[#003b7a] text-white" data-testid="tpl-send-btn">
                      <Send className="h-4 w-4 mr-2" /> Send / Test
                    </Button>
                  </DialogTrigger>
                  <ComposeDialog template={draft} onClose={() => setComposeOpen(false)} />
                </Dialog>
                <Button onClick={save} disabled={busy} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="tpl-save-btn">
                  <Save className="h-4 w-4 mr-2" /> Save
                </Button>
              </div>
            </div>

            <Label className="text-xs">Subject</Label>
            <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="mt-1" data-testid="tpl-subject-input" />

            <Label className="text-xs mt-3 block">HTML body</Label>
            <Textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} className="mt-1 min-h-[260px] font-mono text-xs" data-testid="tpl-body-input" />

            <Label className="text-xs mt-3 block">Available variables</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(draft.variables || []).map((v) => (
                <code key={v} onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)} className="cursor-pointer bg-[#fff8f3] text-[#f37021] border border-[#ffd2b6] rounded px-2 py-1 text-xs hover:bg-[#ffefe6]">
                  {`{{${v}}}`}
                </code>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <Label className="text-xs">Live preview</Label>
              <Button variant="outline" size="sm" onClick={() => refreshPreview(draft)}><Eye className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            </div>
            {preview && (
              <div className="mt-2 rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs text-slate-600">
                  <b>Subject:</b> {preview.subject}
                </div>
                <iframe srcDoc={preview.body_html} title="preview" className="w-full h-[400px] bg-white" data-testid="tpl-preview-iframe" />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function ComposeDialog({ template, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [vars, setVars] = useState(FALLBACK_VARS[template.key] || {});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const list = recipients.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return toast.error("Add at least one recipient");
    setBusy(true);
    try {
      const r = await api.post(`/super-admin/email-templates/${template.key}/send`, {
        template_key: template.key, recipient_emails: list, variables: vars,
      });
      setResult(r.data);
      toast.success(`Sent to ${r.data.sent} recipient(s)${r.data.results.some(x => x.status === "simulated") ? " (simulated)" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-[640px]" data-testid="compose-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Send "{template.name}"</DialogTitle>
        <p className="text-sm text-slate-500">Recipients comma/newline-separated. Real delivery requires RESEND_API_KEY; otherwise it's simulated and logged.</p>
      </DialogHeader>
      <Label className="text-xs">Recipients</Label>
      <Textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="patient@example.com, doctor@apollo.com" className="min-h-[80px]" data-testid="compose-recipients" />
      <Label className="text-xs mt-3 block">Variables (JSON)</Label>
      <Textarea value={JSON.stringify(vars, null, 2)} onChange={(e) => { try { setVars(JSON.parse(e.target.value)); } catch {} }} className="min-h-[120px] font-mono text-xs" data-testid="compose-vars" />
      {result && (
        <div className="mt-2 text-xs bg-emerald-50 text-emerald-700 rounded-lg p-3">
          ✓ Sent to {result.sent} recipient(s). {result.results.filter(r => r.status === "simulated").length} simulated.
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={submit} disabled={busy} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="compose-send-btn">
          {busy ? "Sending…" : "Send now"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
